<template>
  <div class="flex flex-col h-screen bg-white dark:bg-slate-900">
    <!-- Header -->
    <div class="flex items-center gap-3 px-4 py-3 border-b border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 shadow-sm">
      <button
        class="p-1.5 rounded hover:bg-slate-100 dark:hover:bg-slate-800 text-slate-500 dark:text-slate-400"
        @click="$router.back()"
      >
        ←
      </button>
      <div class="flex-1 min-w-0">
        <h1 class="text-sm font-semibold text-slate-900 dark:text-slate-100 truncate">
          Visualizando Backup — Conversa #{{ conversationDisplayId }}
        </h1>
        <p v-if="conversation" class="text-xs text-slate-500 dark:text-slate-400 truncate">
          {{ conversation.inbox_name }} ·
          {{ conversation.contact ? conversation.contact.name : 'Sem contato' }} ·
          {{ formatDate(conversation.created_at) }}
        </p>
      </div>
      <span
        v-if="backup"
        class="text-xs px-2 py-0.5 rounded-full bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-300"
      >
        {{ formatDate(backup.created_at) }}
      </span>
      <!-- Download ZIP -->
      <a
        v-if="backup && backup.zip_url"
        :href="backup.zip_url"
        target="_blank"
        rel="noopener noreferrer"
        class="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded bg-blue-600 hover:bg-blue-700 text-white"
      >
        ⬇ ZIP
      </a>
      <!-- Download PDF -->
      <a
        v-if="backup && backup.pdf_url"
        :href="backup.pdf_url"
        target="_blank"
        rel="noopener noreferrer"
        class="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded border border-slate-300 dark:border-slate-600 text-slate-700 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-800"
      >
        PDF
      </a>
    </div>

    <!-- Loading -->
    <div v-if="loading" class="flex-1 flex items-center justify-center">
      <div class="text-sm text-slate-500 dark:text-slate-400">A carregar backup...</div>
    </div>

    <!-- Error -->
    <div v-else-if="error" class="flex-1 flex items-center justify-center">
      <div class="text-sm text-red-500">{{ error }}</div>
    </div>

    <!-- Messages -->
    <div
      v-else
      ref="messagesContainer"
      class="flex-1 overflow-y-auto px-4 py-4 space-y-3"
    >
      <!-- Conversation info banner -->
      <div
        v-if="conversation"
        class="mx-auto max-w-2xl mb-4 p-3 rounded-lg bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 text-xs text-slate-600 dark:text-slate-400 text-center"
      >
        <span class="font-medium text-slate-800 dark:text-slate-200">Conversa #{{ conversation.display_id }}</span>
        <span v-if="conversation.assignee"> · Agente: {{ conversation.assignee.name }}</span>
        <span> · Status: {{ statusLabel(conversation.status) }}</span>
        <span> · {{ messages.length }} mensagens</span>
      </div>

      <template v-for="msg in messages" :key="msg.id">
        <!-- Activity / system message -->
        <div
          v-if="msg.message_type === 2"
          class="flex justify-center"
        >
          <span class="text-xs text-slate-400 dark:text-slate-500 bg-slate-100 dark:bg-slate-800 px-3 py-1 rounded-full">
            {{ msg.content }}
          </span>
        </div>

        <!-- Outgoing (agent) message — right aligned -->
        <div
          v-else-if="msg.message_type === 1"
          class="flex justify-end"
        >
          <div class="max-w-[70%]">
            <div class="flex items-end gap-1.5 justify-end mb-0.5">
              <span class="text-xs text-slate-400 dark:text-slate-500">{{ formatTime(msg.created_at) }}</span>
              <span class="text-xs font-medium text-blue-600 dark:text-blue-400">
                {{ msg.sender ? msg.sender.name : 'Agente' }}
              </span>
            </div>
            <div
              class="rounded-2xl rounded-br-sm px-3.5 py-2.5 text-sm leading-relaxed"
              :class="msg.private
                ? 'bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 text-amber-900 dark:text-amber-100'
                : 'bg-blue-600 text-white'"
            >
              <span v-if="msg.private" class="text-xs font-medium block mb-1 opacity-70">🔒 Nota privada</span>
              <p class="whitespace-pre-wrap break-words">{{ msg.content }}</p>
              <!-- Attachments -->
              <div v-if="msg.attachments && msg.attachments.length" class="mt-2 space-y-1">
                <a
                  v-for="att in msg.attachments"
                  :key="att.id"
                  :href="att.url"
                  target="_blank"
                  rel="noopener noreferrer"
                  class="flex items-center gap-1.5 text-xs underline opacity-80 hover:opacity-100"
                >
                  <span>📎</span>
                  <span>{{ att.title || att.file_type }}</span>
                </a>
              </div>
            </div>
          </div>
        </div>

        <!-- Incoming (contact) message — left aligned -->
        <div
          v-else
          class="flex justify-start"
        >
          <div class="max-w-[70%]">
            <div class="flex items-end gap-1.5 mb-0.5">
              <span class="text-xs font-medium text-green-700 dark:text-green-400">
                {{ msg.sender ? msg.sender.name : (conversation && conversation.contact ? conversation.contact.name : 'Contato') }}
              </span>
              <span class="text-xs text-slate-400 dark:text-slate-500">{{ formatTime(msg.created_at) }}</span>
            </div>
            <div class="bg-slate-100 dark:bg-slate-800 rounded-2xl rounded-bl-sm px-3.5 py-2.5 text-sm leading-relaxed text-slate-800 dark:text-slate-200">
              <p class="whitespace-pre-wrap break-words">{{ msg.content }}</p>
              <!-- Attachments -->
              <div v-if="msg.attachments && msg.attachments.length" class="mt-2 space-y-1">
                <a
                  v-for="att in msg.attachments"
                  :key="att.id"
                  :href="att.url"
                  target="_blank"
                  rel="noopener noreferrer"
                  class="flex items-center gap-1.5 text-xs text-slate-500 dark:text-slate-400 underline hover:text-slate-700 dark:hover:text-slate-200"
                >
                  <span>📎</span>
                  <span>{{ att.title || att.file_type }}</span>
                </a>
              </div>
            </div>
          </div>
        </div>
      </template>

      <!-- End of conversation -->
      <div class="flex justify-center mt-4">
        <span class="text-xs text-slate-400 dark:text-slate-500 bg-slate-100 dark:bg-slate-800 px-3 py-1 rounded-full">
          Fim do histórico · Backup de {{ formatDate(backup && backup.created_at) }}
        </span>
      </div>
    </div>

    <!-- Read-only footer bar -->
    <div class="px-4 py-3 border-t border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800">
      <div class="flex items-center gap-2 px-3 py-2 rounded-lg bg-white dark:bg-slate-700 border border-slate-200 dark:border-slate-600 text-xs text-slate-400 dark:text-slate-500 cursor-not-allowed select-none">
        <span>🔒</span>
        <span>Esta é uma visualização de backup — não é possível enviar mensagens</span>
      </div>
    </div>
  </div>
</template>

<script>
import backupAPI from '../../../../api/backup';

export default {
  name: 'BackupViewerPage',

  data() {
    return {
      loading: true,
      error: null,
      backup: null,
      conversation: null,
      messages: [],
    };
  },

  computed: {
    conversationDisplayId() {
      return this.$route.params.conversationId || (this.conversation && this.conversation.display_id) || '—';
    },
  },

  async created() {
    await this.loadViewData();
  },

  methods: {
    async loadViewData() {
      this.loading = true;
      this.error = null;
      try {
        const { data } = await backupAPI.getViewData(this.$route.params.backupId);
        this.backup       = data.backup;
        this.conversation = data.conversation;
        this.messages     = data.messages || [];
      } catch (e) {
        this.error = 'Não foi possível carregar o backup. Verifique se ainda existe.';
      } finally {
        this.loading = false;
        this.$nextTick(() => {
          if (this.$refs.messagesContainer) {
            this.$refs.messagesContainer.scrollTop = this.$refs.messagesContainer.scrollHeight;
          }
        });
      }
    },

    statusLabel(status) {
      return { open: 'Aberta', resolved: 'Resolvida', pending: 'Pendente', snoozed: 'Adiada' }[status] || status;
    },

    formatDate(iso) {
      if (!iso) return '—';
      return new Date(iso).toLocaleString('pt-BR', { dateStyle: 'short', timeStyle: 'short' });
    },

    formatTime(iso) {
      if (!iso) return '';
      return new Date(iso).toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' });
    },
  },
};
</script>
